import { create } from 'zustand';
import { practiceService, Practice, PracticeStats } from '@/services/practiceService';

interface PracticeState {
  practices: Practice[];
  currentPractice: Practice | null;
  stats: PracticeStats | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  } | null;
  filters: {
    search?: string;
    type?: string;
    status?: string;
    priority?: string;
    clientId?: string;
    viewMode?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  };
  
  // Actions
  fetchPractices: (params?: any) => Promise<void>;
  fetchPractice: (id: string) => Promise<void>;
  createPractice: (data: Partial<Practice>) => Promise<Practice>;
  updatePractice: (id: string, data: Partial<Practice>) => Promise<void>;
  deletePractice: (id: string, confirmPracticeNumber: string) => Promise<void>;
  archivePractice: (id: string) => Promise<void>;
  fetchStats: () => Promise<void>;
  setFilters: (filters: Partial<PracticeState['filters']>) => void;
  clearFilters: () => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  practices: [],
  currentPractice: null,
  stats: null,
  loading: false,
  error: null,
  pagination: null,
  filters: {},
};

export const usePracticeStore = create<PracticeState>((set, get) => ({
  ...initialState,

  fetchPractices: async (params = {}) => {
    set({ loading: true, error: null });
    try {
      const { filters } = get();
      const mergedParams = { ...params, ...filters };
      const response = await practiceService.getPractices(mergedParams);
      set({
        practices: response.data?.practices || response.data || [],
        pagination: response.data?.pagination || response.pagination || null,
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nel caricamento delle pratiche',
        loading: false,
      });
    }
  },

  fetchPractice: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const response = await practiceService.getPractice(id);
      set({
        currentPractice: response.data?.practice || response.data,
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nel caricamento della pratica',
        loading: false,
      });
    }
  },

  createPractice: async (data: Partial<Practice>) => {
    set({ loading: true, error: null });
    try {
      const response = await practiceService.createPractice(data);
      const newPractice = response.data?.practice || response.data;
      set((state) => ({
        practices: [newPractice, ...state.practices],
        loading: false,
      }));
      return newPractice;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Errore nella creazione della pratica';
      set({ error: errorMsg, loading: false });
      throw error;
    }
  },

  updatePractice: async (id: string, data: Partial<Practice>) => {
    set({ loading: true, error: null });
    try {
      const response = await practiceService.updatePractice(id, data);
      const updatedPractice = response.data?.practice || response.data;
      set((state) => ({
        practices: state.practices.map((p) => (p.id === id ? updatedPractice : p)),
        currentPractice: state.currentPractice?.id === id ? updatedPractice : state.currentPractice,
        loading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nell\'aggiornamento della pratica',
        loading: false,
      });
      throw error;
    }
  },

  deletePractice: async (id: string, confirmPracticeNumber: string) => {
    set({ loading: true, error: null });
    try {
      await practiceService.deletePractice(id, confirmPracticeNumber);
      set((state) => ({
        practices: state.practices.filter((p) => p.id !== id),
        currentPractice: state.currentPractice?.id === id ? null : state.currentPractice,
        loading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nell\'eliminazione della pratica',
        loading: false,
      });
      throw error;
    }
  },

  archivePractice: async (id: string) => {
    set({ loading: true, error: null });
    try {
      await practiceService.archivePractice(id);
      set((state) => ({
        practices: state.practices.map((p) => 
          p.id === id ? { ...p, status: 'ARCHIVED' } : p
        ),
        currentPractice: state.currentPractice?.id === id 
          ? { ...state.currentPractice, status: 'ARCHIVED' }
          : state.currentPractice,
        loading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Errore nell\'archiviazione della pratica',
        loading: false,
      });
      throw error;
    }
  },

  fetchStats: async () => {
    try {
      const response = await practiceService.getPracticeStats();
      set({ stats: response.data || response });
    } catch (error: any) {
      console.error('Errore nel caricamento delle statistiche:', error);
    }
  },

  setFilters: (newFilters: Partial<PracticeState['filters']>) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    }));
  },

  clearFilters: () => {
    set({ filters: {} });
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set(initialState);
  },
}));

